import { Badge, Chip, InfoTip } from '@zarya/ui';
import { t } from '@app/i18n';
import {
  controlCallTip,
  inferredTip,
  isControlCall,
  simulatedTip,
  verdict as verdictBadge,
  verdictInferred,
  type Decision,
} from './vocabulario';
import s from './Decisions.module.css';

export interface VerdictBadgeProps {
  decision: Decision;
}

/**
 * Veredito de uma decisão (T3, T16, T17): `ALLOW` · `DENY` · `indeterminado`,
 * com o chip **inferido** quando o veredito não veio do PDP e sim do status do
 * gateway (FR-097).
 *
 * O chip não é decoração: um DENY inferido pode ser um 403 do gateway por
 * qualquer motivo. Sem o chip, a tela afirmaria que o PDP negou.
 *
 * O mesmo vale para **simulação** (04/09): a decisão é do PDP de verdade, mas a
 * chamada não aconteceu — ninguém pediu aquela tool, ninguém tomou aquele 403.
 * Ela só chega à lista com «Incluir simulações» ligado, e aí o chip é o que
 * impede a linha de ser lida como tráfego.
 *
 * E para a **chamada de controle** (D1, aim-6ph.8): o executor lendo um recurso
 * pelo gateway. Ela só chega com «Incluir chamadas de controle» ligado, com o
 * selo «controle».
 */
export function VerdictBadge({ decision }: VerdictBadgeProps) {
  const { rotulo, tone } = verdictBadge(decision.verdict);
  return (
    <span className={s.veredito}>
      <Badge tone={tone}>{rotulo}</Badge>
      {decision.simulated ? (
        <>
          <Chip icon="info">{t('decisions.badge.simulated')}</Chip>
          <InfoTip text={simulatedTip()} label={t('decisions.badge.simulated.label')} size="xs" />
        </>
      ) : null}
      {isControlCall(decision) ? (
        <>
          <Chip icon="info">{t('decisions.badge.controlCall')}</Chip>
          <InfoTip
            text={controlCallTip()}
            label={t('decisions.badge.controlCall.label')}
            size="xs"
          />
        </>
      ) : null}
      {verdictInferred(decision) ? (
        <>
          <Chip icon="info">{t('decisions.badge.inferred')}</Chip>
          <InfoTip text={inferredTip()} label={t('decisions.badge.inferred.label')} size="xs" />
        </>
      ) : null}
    </span>
  );
}
